import { ChangeEvent, FocusEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Subject } from 'rxjs'

export interface IFormElementHelperProps {
  label?: string
  error?: boolean
  disabled?: boolean
  helperText?: string|undefined
  onBlur?: (ev: FocusEvent<any>) => void
}

export interface IFormElementProps<T> extends IFormElementHelperProps {
  name: string
  value?: T
  onChange: (ev: ChangeEvent<any>) => void
}

export type IFormErrors<T extends Object> = {
  [key in keyof T]?: string|null
}

export type IFormValidators<T extends Object> = {
  [key in keyof T]?: ValidateFunc[]
}

export type ValidateFunc = (value: any) => string|null

export const useForm = <T extends Object>({
  initialValues,
  onSubmit,
  onChange,
  onValid,
  onError,
  validateKeys,
}: {
  initialValues: (() => T)
  onSubmit?: ((result: { values: T, errors: IFormErrors<T> }) => void)
  onChange?: ((value: T) => void)
  onValid?: ((result: { values: T, errors: IFormErrors<T> }) => void)
  onError?: ((result: { values: T, errors: IFormErrors<T> }) => void)
  validateKeys?: (() => IFormValidators<T>)
}) => {
  const [values, setValues] = useState(initialValues || {})
  const values$ = useMemo(() => new Subject<T>(), [])
  const [errors, setErrors] = useState<IFormErrors<T>>({})
  const [touched, setTouched] = useState<{[key in keyof T]?: string[]}|null>(null)
  const [onSubmitting, setOnSubmitting] = useState<boolean>(false)
  const [onBlur, setOnBlur] = useState<boolean>(false)
  const formRendered = useRef(true)

  useEffect(() => {
    if (!formRendered.current) {
      setValues(initialValues)
      setErrors({})
      setTouched({})
      setOnSubmitting(false)
      setOnBlur(false)
    }
    formRendered.current = false
  }, [])

  useEffect(() => {
    if (!touched) { return }
    if (JSON.stringify(errors) === '{}') {
      if (onValid) { onValid({ values, errors }) }
    } else if (onError) {
      onError({ values, errors })
    }
  }, [errors])

  useEffect(() => {
    values$.next(values)
    if (onChange) { onChange(values) }
  }, [values])

  const validate = ({ required, name }: { required: boolean, name?: string }) => {
    const keysToValidate = name ? [name] : Object.keys(values)
    const validators: IFormValidators<T>|null = validateKeys ? validateKeys() : null
    const errorStateChanges: any = {}

    keysToValidate.forEach(key => {
      const value: any = (values as any)[key]
      if (required && !value) { errorStateChanges[key] = `${key} is required`; return }
      if (validators && (validators as any)[key]) {
        const vs: ValidateFunc[] = (validators as any)[key]
        for (const v of vs) {
          const result = v(value)
          if (result !== null) {
            errorStateChanges[key] = result
            return
          }
        }
      }

      // Is valid
      errorStateChanges[key] = undefined
    })

    setErrors({ ...errors, ...errorStateChanges })
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    const { name, value } = target
    event.persist()
    setValues({ ...values, [name]: value })
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { target } = event
    const { name, required } = target
    validate({ name, required })
    setTouched({ ...touched, [name]: true })
  }

  const handleSubmit = (event: any) => {
    if (event) { event.preventDefault() }
    setErrors({ ...errors })
    if (onSubmit) { onSubmit({ values, errors }) }
  }

  const setPartialValues = (change: Partial<T>) => {
    setValues({ ...values, ...change })
  }

  return {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    validate,
    values,
    values$,
    onSubmitting,
    onBlur,
    setValues,
    setPartialValues,
    props(key: keyof T, showValue = true) {
      const v = {
        name: key,
        onChange: handleChange,
        onBlur: handleBlur,
        error: errors[key] ? true : false,
        helperText: errors[key] || undefined,
      } as IFormElementProps<any>

      if (showValue) { v.value = values[key] }
      return v
    },
  }
}

// TODO Make less hacky
export const createSyntheticChangeEvent = <T = any>(name: string, value: T): ChangeEvent<T> => {
  return {
    target: { name, value },
    persist: () => {},
  } as any as ChangeEvent<T>
}
